import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartPrepaymentBlockConfig } from '@front-platform/content-builder';
import { cartPrepaymentBlockResolveConfig } from '@front-platform/content-builder';
import { of, Subject } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { Component, inject, input, computed } from '@angular/core';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CommonModule } from '@angular/common';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { ToggleSwitchAtomComponent } from '@shared/atoms/toggle-switch-atom/toggle-switch-atom.component';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { DisablePrepaymentModalData } from '@shared/modals/prepayment-modal/disable-prepayment-confirm-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrepaidAccountTopUpModalConfig } from '@shared/modals/prepayment-modal/prepaid-account-top-up-modal.component';
import { CartService } from '@core/services/cart/cart.service';
import { NO_PREPAYMENT_COUPON } from '@core/models/cart.model';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';
export interface CartPrepaymentBlockRuntimeConfig {}

export type CartPrepaymentBlockConfigInput = Partial<
  Omit<CartPrepaymentBlockConfig, keyof CartPrepaymentBlockRuntimeConfig> &
    CartPrepaymentBlockRuntimeConfig
>;
export type CartPrepaymentBlockResolvedConfig = Omit<
  CartPrepaymentBlockConfig,
  keyof CartPrepaymentBlockRuntimeConfig
> &
  Partial<CartPrepaymentBlockRuntimeConfig>;
export interface CartPrepaymentBlockContract extends ConfigComponentContract<
  CartPrepaymentBlockConfigInput,
  CartPrepaymentBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-prepayment-block',
  templateUrl: './cart-prepayment-block.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ContentBuilderColorPipe,
    TranslateModule,
    ToggleSwitchAtomComponent,
    ButtonAtomComponent,
    PriceAtomComponent,
  ],
})
export class CartPrepaymentBlockComponent implements CartPrepaymentBlockContract {
  readonly config = input<CartPrepaymentBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartPrepaymentBlockConfig,
    CartPrepaymentBlockRuntimeConfig
  >(cartPrepaymentBlockResolveConfig, () => ({ config: this.config() }));
  private readonly prepaymentSelectorService = inject(PrepaymentSelectorService);

  readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );

  protected readonly orderValidate = this.orderSelector.selectValidation;
  protected readonly orderBalanceBeforeOrder =
    this.prepaymentSelectorService.selectOrderBalanceBeforeOrderInCents;
  protected readonly orderBalanceAfterOrder =
    this.prepaymentSelectorService.selectOrderBalanceAfterOrderInCents;
  protected readonly isLoading = this.prepaymentSelectorService.selectOrderUserBalanceLoading;

  protected readonly isPrepaymentEnabled = this.cartStoreSelector.isPrepaymentEnabled;
  protected readonly userCanUsePrepayment =
    this.prepaymentSelectorService.selectOrderUserCanUsePrepayment;
  protected readonly userHasABalanceAccount =
    this.prepaymentSelectorService.selectOrderUserHasABalanceAccount;

  private readonly refreshOrderValidation$ = new Subject<void>();

  constructor(
    private readonly cartService: CartService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly cartStoreSelector: CartStoreSelector,
    private readonly modalsService: ModalsService<
      [DisablePrepaymentModalData, PrepaidAccountTopUpModalConfig]
    >,
    private readonly orderSelector: OrderStoreSelector,
  ) {
    this.modalsService
      .event$('disable-prepayment-confirm-modal')
      .pipe(
        filter(event => event.type === 'userChoice'),
        takeUntilDestroyed(),
      )
      .subscribe(event => {
        const disablePayment = event.data;

        if (!disablePayment) {
          return;
        }
        this.cartService.addCoupon(NO_PREPAYMENT_COUPON);
        this.refreshOrderValidation$.next();
      });

    this.refreshOrderValidation$
      .pipe(
        switchMap(() => this.orderDispatcher.checkCartIsValid$()),
        catchError(() => of(undefined)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  openTopUpModal() {
    const shopId = this.prepaymentSelectorService.selectOrderActiveShopId();
    if (!shopId) {
      return;
    }
    this.modalsService.open('prepaid-account-top-up-modal', undefined, {
      shopId,
    });
  }

  onToggleChange(value: boolean) {
    if (!value) {
      this.modalsService.open('disable-prepayment-confirm-modal');
      return;
    }

    this.cartService.removeCoupon(NO_PREPAYMENT_COUPON);
    this.refreshOrderValidation$.next();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartPrepaymentBlockComponent, { 'keyAliases': {} });
